import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import type { Nutrient } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FormField,
  emptyToUndef as _emptyToUndef,
  numToStr,
  parseOptNum as _parseOptNum,
} from "@/components/forms/field";

const optionalNumString = z
  .string()
  .optional()
  .refine(
    (v) => v === undefined || v === "" || !Number.isNaN(Number(v)),
    "must be a number",
  );

export const nutrientSchema = z.object({
  name: z.string().min(1, "name is required"),
  manufacturer: z.string().optional(),
  base_dose_ml_per_gal: optionalNumString,
  delta_ec_per_ml_per_gal: optionalNumString,
  delta_ph_per_ml_per_gal: optionalNumString,
  mixing_order_rank: z
    .string()
    .min(1, "required")
    .refine(
      (v) => Number.isInteger(Number(v)) && Number(v) >= 0,
      "must be a non-negative integer",
    ),
  contains_calcium: z.boolean().optional(),
  contains_phosphate: z.boolean().optional(),
  is_biological: z.boolean().optional(),
  is_ph_adjuster: z.boolean().optional(),
  notes: z.string().optional(),
  reason: z.string().optional(),
});

export type NutrientFormValues = z.infer<typeof nutrientSchema>;

export const parseOptNum = _parseOptNum;
export const emptyToUndef = _emptyToUndef;

export interface NutrientFormProps {
  initial?: Nutrient;
  submitting: boolean;
  onSubmit: (values: NutrientFormValues) => void;
  banner?: ReactNode;
  submitLabel?: string;
  showReason?: boolean;
}

export function NutrientForm({
  initial,
  submitting,
  onSubmit,
  banner,
  submitLabel,
  showReason = false,
}: NutrientFormProps) {
  const form = useForm<NutrientFormValues>({
    resolver: zodResolver(nutrientSchema),
    defaultValues: defaultsFor(initial),
  });
  const err = form.formState.errors;
  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-6 max-w-2xl"
      noValidate
    >
      {banner}

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Name" error={err.name?.message}>
          <Input autoFocus {...form.register("name")} />
        </FormField>
        <FormField label="Manufacturer">
          <Input {...form.register("manufacturer")} />
        </FormField>
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Signatures</legend>
        <div className="grid grid-cols-3 gap-3">
          <FormField
            label="base dose (ml/gal)"
            error={err.base_dose_ml_per_gal?.message}
          >
            <Input
              type="number"
              step="any"
              {...form.register("base_dose_ml_per_gal")}
            />
          </FormField>
          <FormField
            label="ΔEC/ml/gal"
            error={err.delta_ec_per_ml_per_gal?.message}
          >
            <Input
              type="number"
              step="any"
              {...form.register("delta_ec_per_ml_per_gal")}
            />
          </FormField>
          <FormField
            label="ΔpH/ml/gal"
            error={err.delta_ph_per_ml_per_gal?.message}
          >
            <Input
              type="number"
              step="any"
              {...form.register("delta_ph_per_ml_per_gal")}
            />
          </FormField>
        </div>
      </fieldset>

      <FormField
        label="Mixing order rank"
        help="0 mixes first; ties break alphabetically."
        error={err.mixing_order_rank?.message}
      >
        <Input
          type="number"
          min={0}
          step={1}
          {...form.register("mixing_order_rank")}
        />
      </FormField>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Flags</legend>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <CheckboxRow
            label="contains calcium"
            {...form.register("contains_calcium")}
          />
          <CheckboxRow
            label="contains phosphate"
            {...form.register("contains_phosphate")}
          />
          <CheckboxRow
            label="biological (compost tea, beneficials)"
            {...form.register("is_biological")}
          />
          <CheckboxRow
            label="pH adjuster"
            {...form.register("is_ph_adjuster")}
          />
        </div>
      </fieldset>

      <FormField label="Notes">
        <Textarea rows={3} {...form.register("notes")} />
      </FormField>

      {showReason && (
        <FormField
          label="Reason for change"
          help="Captured in the audit log."
        >
          <Input {...form.register("reason")} />
        </FormField>
      )}

      <Button type="submit" disabled={submitting}>
        {submitting ? "Saving…" : (submitLabel ?? "Save")}
      </Button>
    </form>
  );
}

// CheckboxRow is a label + checkbox; we don't pull in shadcn's Checkbox
// component to keep the bundle lean. The forwarded register props attach
// to the underlying <input>.
import { forwardRef, type InputHTMLAttributes } from "react";

const CheckboxRow = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label: string }
>(function CheckboxRow({ label, ...rest }, ref) {
  return (
    <label className="flex items-center gap-2">
      <input type="checkbox" ref={ref} {...rest} />
      <span>{label}</span>
    </label>
  );
});

function defaultsFor(n: Nutrient | undefined): NutrientFormValues {
  if (!n) {
    return {
      name: "",
      manufacturer: "",
      mixing_order_rank: "0",
      notes: "",
    };
  }
  return {
    name: n.name,
    manufacturer: n.manufacturer ?? "",
    base_dose_ml_per_gal: numToStr(n.base_dose_ml_per_gal),
    delta_ec_per_ml_per_gal: numToStr(n.delta_ec_per_ml_per_gal),
    delta_ph_per_ml_per_gal: numToStr(n.delta_ph_per_ml_per_gal),
    mixing_order_rank: String(n.mixing_order_rank),
    contains_calcium: n.contains_calcium,
    contains_phosphate: n.contains_phosphate,
    is_biological: n.is_biological,
    is_ph_adjuster: n.is_ph_adjuster,
    notes: n.notes ?? "",
  };
}
